import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useUserStore } from '@/stores/userStore'
import * as dashboardApi from '@/api/dashboardApi'

/**
 * Store для управления правами текущего открытого проекта (уровень 3: канбан)
 * Содержит полные детальные права для работы с UI проекта
 *
 * API: GET api/v1/project/template?id={projectId}
 * Ответ: { params: {...}, role_title: string, tariff: {...} }
 */
export const useProjectPermissionsStore = defineStore('projectPermissions', () => {
  // === State ===
  const currentProjectId = ref(null)
  const permissions = ref(null) // Полный объект params с правами
  const role = ref(null) // role_title
  const tariff = ref(null) // Тариф проекта
  const isLoading = ref(false)
  const loadedAt = ref(null)

  // Кэш загруженных проектов (projectId -> data)
  const cache = ref(new Map())
  const TTL = 5 * 60 * 1000 // 5 минут

  // === UserStore ===

  let _userStore = null
  const isUserStoreReady = computed(() => _userStore && _userStore.token !== null)

  /**
   * Ожидание выполнения условия
   */
  async function waitForCondition(condition, timeout = 3000, interval = 100) {
    const start = Date.now()

    while (Date.now() - start < timeout) {
      if (condition()) return true
      await new Promise(resolve => setTimeout(resolve, interval))
    }

    throw new Error('Condition not met within timeout')
  }

  /**
   * Гарантирует инициализацию userStore
   */
  async function ensureUserStore() {
    if (!_userStore) {
      _userStore = useUserStore()
    }
    await waitForCondition(() => isUserStoreReady.value)
  }

  // === Getters ===

  /**
   * Проверка инициализации (есть ли загруженные права)
   */
  const isInitialized = computed(() => {
    return currentProjectId.value !== null && permissions.value !== null
  })

  /**
   * Получить значение конкретного права (raw value)
   */
  const getPermission = computed(() => (permissionName) => {
    if (!permissions.value) return null
    return permissions.value[permissionName]?.value ?? null
  })

  /**
   * Получить полный объект права (value, type, description, name, enumValues)
   */
  const getPermissionObject = computed(() => (permissionName) => {
    if (!permissions.value) return null
    return permissions.value[permissionName] || null
  })

  /**
   * Проверка наличия права
   *
   * Логика:
   * - boolean: value === true → доступ есть
   * - enum full: всегда доступ (при contextual: true — не зависит от extraCondition)
   * - enum limited:
   *   - без contextual: доступ есть (true)
   *   - с contextual: true: зависит от extraCondition
   * - enum none: нет доступа
   *
   * @param {string} permissionName - название права
   * @param {object} options - опции проверки
   * @param {boolean} options.contextual - контекстный режим (для limited)
   * @param {any} options.extraCondition - дополнительное условие
   * @param {string} options.expectedValue - ожидаемое значение (для точного сравнения)
   */
  const hasPermission = computed(() => (permissionName, options = {}) => {
    if (!permissions.value) return false

    const permission = permissions.value[permissionName]
    if (!permission) return false

    const { contextual = false, extraCondition, expectedValue } = options

    // Разворачиваем extraCondition (может быть ref)
    const resolveCondition = (cond) => {
      if (cond === undefined) return undefined
      if (typeof cond === 'object' && cond !== null && 'value' in cond) {
        return cond.value
      }
      return cond
    }

    const condValue = resolveCondition(extraCondition)

    // Если указано ожидаемое значение - точное сравнение
    if (expectedValue !== undefined) {
      return permission.value === expectedValue
    }

    // Проверка по типу
    if (permission.type === 'boolean') {
      // Нормализация: 'true' → true, 'false' → false
      const boolValue = permission.value === true || permission.value === 'true'
      return boolValue
    }

    if (permission.type === 'enum') {
      const enumValue = permission.value

      // full - всегда доступно
      if (enumValue === 'full') {
        return true
      }

      // none - никогда не доступно
      if (enumValue === 'none') {
        return false
      }

      // limited - зависит от контекста
      if (enumValue === 'limited') {
        // Без contextual: считаем что доступ есть
        if (!contextual) {
          return true
        }

        // С contextual: зависит от extraCondition
        if (condValue !== undefined) {
          return Boolean(condValue)
        }

        return false
      }

      // Другие enum значения - проверяем что не none
      return enumValue !== 'none' && enumValue !== 'false' && enumValue !== false
    }

    // Fallback для неизвестных типов
    const val = permission.value
    if (typeof val === 'boolean') return val
    if (typeof val === 'string') return val !== 'none' && val !== 'false'
    return Boolean(val)
  })

  /**
   * Проверка роли
   */
  const hasRole = computed(() => (roleName) => {
    return role.value === roleName
  })

  /**
   * Проверка тарифа
   */
  const hasTariff = computed(() => (tariffTag) => {
    return tariff.value?.tag === tariffTag
  })

  /**
   * Получить фичу из тарифа
   */
  const getTariffFeature = computed(() => (featureName) => {
    return tariff.value?.params?.templates?.[featureName]?.value
  })

  /**
   * Получить лимит из тарифа
   */
  const getTariffLimit = computed(() => (limitName) => {
    return tariff.value?.params?.limits?.[limitName]?.value
  })

  /**
   * Проверка актуальности кэша для проекта
   */
  const isProjectCacheStale = computed(() => (projectId) => {
    const cached = cache.value.get(projectId)
    if (!cached) return true
    return Date.now() - cached.loadedAt > TTL
  })

  // === Actions ===

  /**
   * Загрузить права для проекта
   * @param {number} projectId - ID проекта
   * @param {boolean} force - принудительная загрузка (игнорируя кэш)
   */
  async function loadForProject(projectId, force = false) {
    // Проверяем кэш
    if (!force && cache.value.has(projectId)) {
      const cached = cache.value.get(projectId)
      if (Date.now() - cached.loadedAt < TTL) {
        // Кэш актуален - используем его
        setCurrentProject(projectId, cached.data)
        return cached.data
      }
    }

    isLoading.value = true
    try {
      // Ждем пока userStore будет готов
      await ensureUserStore()

      const data = await dashboardApi.getProjectRights(
        _userStore.token,
        projectId
      )

      if (!data) {
        console.error('Failed to load project rights: empty response')
        return null
      }

      // Сохраняем в кэш
      cache.value.set(projectId, {
        data,
        loadedAt: Date.now()
      })

      // Устанавливаем как текущий
      setCurrentProject(projectId, data)

      return data
    } catch (error) {
      console.error('Failed to load project rights:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Установить текущий проект (внутренний метод)
   */
  function setCurrentProject(projectId, data) {
    currentProjectId.value = projectId
    permissions.value = data.params || null
    role.value = data.role_title || null
    tariff.value = data.tariff || null
    loadedAt.value = Date.now()
  }

  /**
   * Частичное обновление прав текущего проекта
   * Мержит только измененные поля, не заменяет всё
   */
  function updatePartial(updates) {
    if (!currentProjectId.value) {
      console.warn('No current project to update')
      return
    }

    // Обновляем permissions (params)
    if (updates.params) {
      permissions.value = deepMerge(permissions.value || {}, updates.params)
    }

    // Обновляем role
    if (updates.role_title !== undefined) {
      role.value = updates.role_title
    }

    // Обновляем tariff
    if (updates.tariff) {
      tariff.value = deepMerge(tariff.value || {}, updates.tariff)
    }

    // Обновляем кэш
    const cached = cache.value.get(currentProjectId.value)
    if (cached) {
      cached.data = {
        ...cached.data,
        ...updates
      }
      cached.loadedAt = Date.now()
    }

    loadedAt.value = Date.now()
  }

  /**
   * Обновить конкретное право
   */
  function updatePermission(permissionName, value) {
    if (!permissions.value || !permissions.value[permissionName]) {
      console.warn(`Permission ${permissionName} not found`)
      return
    }

    permissions.value[permissionName].value = value
    loadedAt.value = Date.now()

    // Обновляем кэш
    if (currentProjectId.value) {
      const cached = cache.value.get(currentProjectId.value)
      if (cached?.data?.params?.[permissionName]) {
        cached.data.params[permissionName].value = value
      }
    }
  }

  /**
   * Очистить текущий проект
   */
  function clearCurrent() {
    currentProjectId.value = null
    permissions.value = null
    role.value = null
    tariff.value = null
    loadedAt.value = null
    isLoading.value = false
  }

  /**
   * Очистить весь store и кэш
   */
  function clear() {
    clearCurrent()
    cache.value.clear()
    _userStore = null
  }

  /**
   * Очистить кэш для конкретного проекта
   */
  function clearCache(projectId) {
    if (projectId) {
      cache.value.delete(projectId)
    } else {
      cache.value.clear()
    }
  }

  return {
    // State
    currentProjectId,
    permissions,
    role,
    tariff,
    isLoading,
    loadedAt,

    // Getters
    isInitialized,
    getPermission,
    getPermissionObject,
    hasPermission,
    hasRole,
    hasTariff,
    getTariffFeature,
    getTariffLimit,
    isProjectCacheStale,

    // Actions
    loadForProject,
    setCurrentProject,
    updatePartial,
    updatePermission,
    clearCurrent,
    clear,
    clearCache
  }
})

// === Helpers ===

/**
 * Глубокое слияние объектов
 */
function deepMerge(target, source) {
  const output = { ...target }

  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (target[key] && typeof target[key] === 'object') {
        output[key] = deepMerge(target[key], source[key])
      } else {
        output[key] = source[key]
      }
    } else {
      output[key] = source[key]
    }
  }

  return output
}
